import type { CountrySettings } from "@/config/country-settings";

export enum StepType {
	Landing = "landing",
	AboutLoan = "about-loan",
	PersonalInformationContact = "personal-information-contact",
	PersonalInformationCivil = "personal-information-civil",
	EconomyEmployment = "economy-employment",
	EconomyCars = "economy-cars",
	EconomyDebt = "economy-debt",
	EconomyResidence = "economy-residence",
	CoApplicantChoice = "co-applicant-choice",
	CoApplicantContact = "co-applicant-contact",
	CoApplicantCivil = "co-applicant-civil",
	CoApplicantEmployment = "co-applicant-employment",
	CoApplicantCars = "co-applicant-cars",
	CoApplicantDebt = "co-applicant-debt",
	CoApplicantResidence = "co-applicant-residence",
	Summary = "summary",
	Submitted = "submitted",
}

export const stepOrder: StepType[] = [
	StepType.Landing,
	StepType.AboutLoan,
	StepType.PersonalInformationContact,
	StepType.PersonalInformationCivil,
	StepType.EconomyEmployment,
	StepType.EconomyCars,
	StepType.EconomyDebt,
	StepType.EconomyResidence,
	StepType.CoApplicantChoice,
	StepType.CoApplicantContact,
	StepType.CoApplicantCivil,
	StepType.CoApplicantEmployment,
	StepType.CoApplicantCars,
	StepType.CoApplicantDebt,
	StepType.CoApplicantResidence,
	StepType.Summary,
	StepType.Submitted,
];

/** Step cookie values written by older releases, still present in the wild. */
const LEGACY_STEP_ALIAS: Record<string, StepType> = {
	"personal-information": StepType.PersonalInformationContact,
	economy: StepType.EconomyEmployment,
};

export const normalizeStep = (value?: string): StepType | undefined => {
	if (!value) return undefined;

	return LEGACY_STEP_ALIAS[value] ?? stepOrder.find((step) => step === value);
};

/**
 * Number stored in Goldfinger `commonData.step`. Persisted: never renumber or
 * reuse a number, even when the flow changes. A new step gets the next free one.
 */
const STEP_NUMBER = {
	[StepType.Landing]: 0,
	[StepType.AboutLoan]: 1,
	[StepType.PersonalInformationContact]: 2,
	[StepType.PersonalInformationCivil]: 3,
	[StepType.EconomyEmployment]: 4,
	[StepType.EconomyCars]: 5,
	[StepType.EconomyDebt]: 6,
	[StepType.EconomyResidence]: 7,
	[StepType.CoApplicantChoice]: 8,
	[StepType.CoApplicantContact]: 9,
	[StepType.CoApplicantCivil]: 10,
	[StepType.CoApplicantEmployment]: 11,
	[StepType.CoApplicantCars]: 12,
	[StepType.CoApplicantDebt]: 13,
	[StepType.CoApplicantResidence]: 14,
	[StepType.Summary]: 15,
	[StepType.Submitted]: 16,
} as const satisfies Record<StepType, number>;

export const getStepNumber = (step: StepType): number => STEP_NUMBER[step];

export const getStepByNumber = (value?: number | null): StepType | undefined =>
	stepOrder.find((step) => STEP_NUMBER[step] === value);

export const getStepFromStoredApplication = (
	storedStep?: number | null,
	cookieStep?: StepType
): StepType | undefined => {
	const persistedStep = getStepByNumber(storedStep);
	if (persistedStep === StepType.Submitted) return StepType.Summary;

	return persistedStep ?? cookieStep;
};

/**
 * Stable, locale-independent step names used in Datadog log messages, e.g.
 * `Task[loan.update], Step: Residence, Message: Updated application data`.
 */
const STEP_LOG_LABEL: Record<StepType, string> = {
	[StepType.Landing]: "Landing",
	[StepType.AboutLoan]: "AboutLoan",
	[StepType.PersonalInformationContact]: "PersonalInfo",
	[StepType.PersonalInformationCivil]: "CivilStatus",
	[StepType.EconomyEmployment]: "Employment",
	[StepType.EconomyCars]: "Cars",
	[StepType.EconomyDebt]: "Debt",
	[StepType.EconomyResidence]: "Residence",
	[StepType.CoApplicantChoice]: "CoApplicantChoice",
	[StepType.CoApplicantContact]: "CoApplicantPersonalInfo",
	[StepType.CoApplicantCivil]: "CoApplicantCivilStatus",
	[StepType.CoApplicantEmployment]: "CoApplicantEmployment",
	[StepType.CoApplicantCars]: "CoApplicantCars",
	[StepType.CoApplicantDebt]: "CoApplicantDebt",
	[StepType.CoApplicantResidence]: "CoApplicantResidence",
	[StepType.Summary]: "Summary",
	[StepType.Submitted]: "Submitted",
};

export const getStepLogLabel = (value?: string): string | undefined => {
	const step = normalizeStep(value);
	return step && STEP_LOG_LABEL[step];
};

/**
 * A user-facing wizard step. Each maps 1:1 to a feature folder and to a section
 * label key. Co-applicant choice + contact collapse into a single `coApplicant`
 * step; Summary is a review screen and is intentionally not a feature step.
 */
export type StepFeature =
	| "aboutLoan"
	| "personalInfo"
	| "civilStatus"
	| "employment"
	| "cars"
	| "debt"
	| "residence"
	| "coApplicant";

const STEP_FEATURE: Partial<Record<StepType, StepFeature>> = {
	[StepType.AboutLoan]: "aboutLoan",
	[StepType.PersonalInformationContact]: "personalInfo",
	[StepType.PersonalInformationCivil]: "civilStatus",
	[StepType.EconomyEmployment]: "employment",
	[StepType.EconomyCars]: "cars",
	[StepType.EconomyDebt]: "debt",
	[StepType.EconomyResidence]: "residence",
	[StepType.CoApplicantChoice]: "coApplicant",
	[StepType.CoApplicantContact]: "coApplicant",
	[StepType.CoApplicantCivil]: "coApplicant",
	[StepType.CoApplicantEmployment]: "coApplicant",
	[StepType.CoApplicantCars]: "coApplicant",
	[StepType.CoApplicantDebt]: "coApplicant",
	[StepType.CoApplicantResidence]: "coApplicant",
};

export const getStepFeature = (step: StepType): StepFeature | undefined =>
	STEP_FEATURE[step];

/**
 * Section label (reusing the main stepper vocabulary) shown in the co-applicant
 * sub-flow header, e.g. `CoApplicantContact` -> "personalInfo" -> "Kontaktuppgifter".
 */
const CO_APPLICANT_STEP_SECTION: Partial<Record<StepType, StepFeature>> = {
	[StepType.CoApplicantContact]: "personalInfo",
	[StepType.CoApplicantCivil]: "civilStatus",
	[StepType.CoApplicantEmployment]: "employment",
	[StepType.CoApplicantCars]: "cars",
	[StepType.CoApplicantDebt]: "debt",
	[StepType.CoApplicantResidence]: "residence",
};

export const getCoApplicantStepSection = (
	step: StepType
): StepFeature | undefined => CO_APPLICANT_STEP_SECTION[step];

/** Flow position of a step for this country, from Landing to Submitted. */
export const getStepSequence = (
	settings: Pick<CountrySettings, "collectsCarsAndDebt">,
	hasCoApplicant: boolean
): StepType[] => {
	const carsAndDebt = settings.collectsCarsAndDebt;
	return [
		StepType.Landing,
		StepType.AboutLoan,
		StepType.PersonalInformationContact,
		StepType.PersonalInformationCivil,
		StepType.EconomyEmployment,
		...(carsAndDebt ? [StepType.EconomyCars, StepType.EconomyDebt] : []),
		StepType.EconomyResidence,
		StepType.CoApplicantChoice,
		...(hasCoApplicant
			? [
					StepType.CoApplicantContact,
					StepType.CoApplicantCivil,
					StepType.CoApplicantEmployment,
					...(carsAndDebt
						? [StepType.CoApplicantCars, StepType.CoApplicantDebt]
						: []),
					StepType.CoApplicantResidence,
				]
			: []),
		StepType.Summary,
		StepType.Submitted,
	];
};

/** `undefined` when the step is last or not in the sequence. */
export const getNextStep = (
	sequence: readonly StepType[],
	step: StepType
): StepType | undefined => {
	const index = sequence.indexOf(step);
	return index === -1 ? undefined : sequence[index + 1];
};

/** `undefined` when the step is first or not in the sequence. */
export const getPreviousStep = (
	sequence: readonly StepType[],
	step: StepType
): StepType | undefined => {
	const index = sequence.indexOf(step);
	return index <= 0 ? undefined : sequence[index - 1];
};

/**
 * Ordered co-applicant sub-flow steps, used by the simplified sub-stepper. The
 * choice gate is intentionally excluded — it precedes the sub-flow.
 */
export const getCoApplicantStepOrder = (
	settings: Pick<CountrySettings, "collectsCarsAndDebt">
): StepType[] =>
	getStepSequence(settings, true).filter(
		(step) => getCoApplicantStepSection(step) !== undefined
	);

/** Ordered feature steps shown in the stepper. */
export const getStepFeatureOrder = (
	settings: Pick<CountrySettings, "collectsCarsAndDebt">
): StepFeature[] => [
	...new Set(
		getStepSequence(settings, false).flatMap((step) => {
			const feature = getStepFeature(step);
			return feature ? [feature] : [];
		})
	),
];
